import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client';
import { useNotification } from '../components/Notification';
import { useLanguage } from '../context/LanguageContext';
import './AdminPaymentMethods.css';

function AdminPaymentMethods() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const { t, language } = useLanguage();
  const [editingMethod, setEditingMethod] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [quickImageTarget, setQuickImageTarget] = useState(null);
  const fileInputRef = useRef(null);
  const quickImageInputRef = useRef(null);
  const [editForm, setEditForm] = useState({
    is_enabled: false,
    is_test_mode: true,
    config_data: {},
    display_order: 0,
    name_lt: '',
    name_en: '',
    name_de: '',
    display_mode: 'name_and_image',
    image_url: ''
  });

  const { data: methods, isLoading } = useQuery({
    queryKey: ['payment-methods-admin'],
    queryFn: async () => {
      const res = await apiClient.get('/payment-methods/admin');
      return res.data;
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      return apiClient.put(`/payment-methods/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['payment-methods-admin']);
      setEditingMethod(null);
      notification.success(t('paymentMethodSuccessUpdate'));
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || t('paymentMethodErrorUpdate'));
    },
  });

  const handleEdit = (method) => {
    setEditingMethod(method);
    let config_data = method.config_data || {};
    if (method.method_key === 'bank_transfer') {
      const accounts = Array.isArray(config_data.bank_accounts)
        ? config_data.bank_accounts
        : (config_data.iban
            ? [{ iban: config_data.iban, bank_name: config_data.bank_name || '', company_name: config_data.company_name || '' }]
            : [{ iban: '', bank_name: '', company_name: '' }]);
      config_data = { ...config_data, bank_accounts: accounts };
    }
    setEditForm({
      is_enabled: method.is_enabled || false,
      is_test_mode: method.is_test_mode !== undefined ? method.is_test_mode : true,
      config_data,
      display_order: method.display_order || 0,
      name_lt: method.name_lt ?? method.method_name ?? '',
      name_en: method.name_en ?? '',
      name_de: method.name_de ?? '',
      display_mode: method.display_mode || 'name_and_image',
      image_url: method.image_url || ''
    });
  };

  /** Mokėjimo priemonės pavadinimas pagal kalbą (admin sąraše) */
  const getMethodDisplayName = (method) => {
    if (language === 'en' && method.name_en) return method.name_en;
    if (language === 'de' && method.name_de) return method.name_de;
    return method.name_lt || method.method_name || method.method_key || '—';
  };

  const handleSave = () => {
    if (!editingMethod) return;

    let cleanedConfigData = {};
    if (editingMethod.method_key === 'bank_transfer') {
      const accounts = (editForm.config_data?.bank_accounts || []).filter(
        row => (row.iban || '').trim() || (row.bank_name || '').trim() || (row.company_name || '').trim()
      ).map(row => ({
        iban: (row.iban || '').trim(),
        bank_name: (row.bank_name || '').trim(),
        company_name: (row.company_name || '').trim()
      }));
      cleanedConfigData = {
        bank_accounts: accounts,
        reference_prefix: (editForm.config_data?.reference_prefix || '').trim() || undefined,
        instructions_lt: (editForm.config_data?.instructions_lt || '').trim() || undefined,
        instructions_en: (editForm.config_data?.instructions_en || '').trim() || undefined,
        instructions_de: (editForm.config_data?.instructions_de || '').trim() || undefined
      };
      Object.keys(cleanedConfigData).forEach(k => { if (cleanedConfigData[k] === undefined) delete cleanedConfigData[k]; });
    } else if (editForm.config_data && typeof editForm.config_data === 'object') {
      Object.keys(editForm.config_data).forEach(key => {
        const value = editForm.config_data[key];
        if (value !== null && value !== undefined && value !== '') {
          cleanedConfigData[key] = value;
        }
      });
    }
    
    const dataToSend = {
      ...editForm,
      config_data: Object.keys(cleanedConfigData).length > 0 ? cleanedConfigData : null,
      name_lt: (editForm.name_lt || '').trim() || null,
      name_en: (editForm.name_en || '').trim() || null,
      name_de: (editForm.name_de || '').trim() || null,
      display_mode: editForm.display_mode || 'name_and_image',
      image_url: (editForm.image_url || '').trim() || null
    };
    
    updateMutation.mutate({
      id: editingMethod.id,
      data: dataToSend
    });
  };

  const handleConfigChange = (key, value) => {
    setEditForm(prev => ({
      ...prev,
      config_data: {
        ...prev.config_data,
        [key]: value
      }
    }));
  };

  /** Įkelti paveikslėlį iš kompiuterio į serverį */
  const handleImageUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file || !file.type.startsWith('image/')) {
      notification.error(t('paymentMethodImageInvalid'));
      return;
    }
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await apiClient.post('/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const imageUrl = res.data?.imageUrl;
      if (imageUrl) {
        setEditForm(prev => ({ ...prev, image_url: imageUrl }));
        notification.success(t('paymentMethodImageUploaded'));
      } else {
        notification.error(t('paymentMethodImageUploadError'));
      }
    } catch (err) {
      notification.error(err.response?.data?.error || t('paymentMethodImageUploadError'));
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeImage = () => {
    setEditForm(prev => ({ ...prev, image_url: '' }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleQuickImageUpload = async (e, key) => {
    const file = e?.target?.files?.[0];
    if (!file || !file.type.startsWith('image/')) {
      notification.error(t('paymentMethodImageInvalid'));
      setQuickImageTarget(null);
      return;
    }
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await apiClient.post('/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const imageUrl = res.data?.imageUrl;
      if (imageUrl) {
        handleConfigChange(key, imageUrl);
        notification.success(t('paymentMethodImageUploaded'));
      } else {
        notification.error(t('paymentMethodImageUploadError'));
      }
    } catch (err) {
      notification.error(err.response?.data?.error || t('paymentMethodImageUploadError'));
    } finally {
      setUploadingImage(false);
      setQuickImageTarget(null);
      if (quickImageInputRef.current) quickImageInputRef.current.value = '';
    }
  };

  const getMethodConfigFields = (methodKey) => {
    switch (methodKey) {
      case 'stripe':
        return [
          { key: 'test_secret_key', labelKey: 'paymentConfigTestSecretKey', type: 'password' },
          { key: 'test_publishable_key', labelKey: 'paymentConfigTestPublishableKey', type: 'text' },
          { key: 'live_secret_key', labelKey: 'paymentConfigLiveSecretKey', type: 'password' },
          { key: 'live_publishable_key', labelKey: 'paymentConfigLivePublishableKey', type: 'text' },
        ];
      case 'paypal':
        return [
          { key: 'client_id', labelKey: 'paymentConfigClientId', type: 'text' },
          { key: 'client_secret', labelKey: 'paymentConfigClientSecret', type: 'password' },
          { key: 'sandbox', labelKey: 'paymentConfigSandboxMode', type: 'checkbox' },
        ];
      case 'banklink':
        return [
          { key: 'merchant_id', labelKey: 'paymentConfigMerchantId', type: 'text' },
          { key: 'private_key', labelKey: 'paymentConfigPrivateKey', type: 'textarea' },
          { key: 'bank_url', labelKey: 'paymentConfigBankUrl', type: 'text' },
        ];
      case 'bank_transfer':
        return [];
      default:
        return [];
    }
  };

  /** Banko pavedimas: sąrašas banko sąskaitų (dinaminis) */
  const bankAccounts = Array.isArray(editForm.config_data?.bank_accounts)
    ? editForm.config_data.bank_accounts
    : (editForm.config_data?.iban
        ? [{ iban: editForm.config_data.iban, bank_name: editForm.config_data.bank_name || '', company_name: editForm.config_data.company_name || '' }]
        : [{ iban: '', bank_name: '', company_name: '' }]);

  const setBankAccounts = (next) => {
    setEditForm(prev => ({
      ...prev,
      config_data: { ...prev.config_data, bank_accounts: next },
      ...(next.length === 0 ? {} : {})
    }));
  };

  const addBankAccount = () => {
    setBankAccounts([...bankAccounts, { iban: '', bank_name: '', company_name: '' }]);
  };

  const updateBankAccount = (index, field, value) => {
    const next = bankAccounts.map((row, i) =>
      i === index ? { ...row, [field]: value } : row
    );
    setBankAccounts(next);
  };

  const removeBankAccount = (index) => {
    setBankAccounts(bankAccounts.filter((_, i) => i !== index));
  };

  if (isLoading) {
    return <div className="admin-payment-methods-page container">{t('loading')}</div>;
  }

  return (
    <div className="admin-payment-methods-page container">
      <div className="methods-header">
        <h1>{t('paymentMethodsTitle')}</h1>
        <p className="subtitle">
          {t('paymentMethodsSubtitle')}
        </p>
      </div>

      <div className="methods-list">
        {methods?.map((method) => (
          <div key={method.id} className="method-card">
            <div className="method-header">
              <div className="method-info">
                <h3>{getMethodDisplayName(method)}</h3>
                <span className={`method-key`}>{method.method_key}</span>
              </div>
              <div className="method-status">
                <span className={`status-badge ${method.is_enabled ? 'enabled' : 'disabled'}`}>
                  {method.is_enabled ? t('paymentMethodActive') : t('paymentMethodInactive')}
                </span>
                {method.is_test_mode && (
                  <span className="test-mode-badge">{t('paymentMethodTestMode')}</span>
                )}
              </div>
            </div>

            <div className="method-actions">
              <button
                onClick={() => handleEdit(method)}
                className="btn btn-primary"
              >
                {t('paymentMethodEdit')}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Redagavimo modalas */}
      {editingMethod && (
        <div className="modal-overlay" onClick={() => setEditingMethod(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{t('paymentMethodEditTitle').replace('{name}', getMethodDisplayName(editingMethod))}</h2>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={editForm.is_enabled}
                  onChange={(e) => setEditForm({ ...editForm, is_enabled: e.target.checked })}
                />
                <span>{t('paymentMethodEnable')}</span>
              </label>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={editForm.is_test_mode}
                  onChange={(e) => setEditForm({ ...editForm, is_test_mode: e.target.checked })}
                />
                <span>{t('paymentMethodTestModeLabel')}</span>
              </label>
            </div>

            <div className="form-group">
              <label>{t('paymentMethodSortOrder')}</label>
              <input
                type="number"
                value={editForm.display_order}
                onChange={(e) => setEditForm({ ...editForm, display_order: parseInt(e.target.value) || 0 })}
                className="input"
                min="0"
              />
            </div>

            <div className="form-group">
              <p className="form-hint" style={{ marginBottom: '10px', fontSize: '12px', color: '#666' }}>{t('paymentMethodNameHint')}</p>
              <label>{t('paymentMethodNameLt')}</label>
              <input
                type="text"
                value={editForm.name_lt || ''}
                onChange={(e) => setEditForm({ ...editForm, name_lt: e.target.value })}
                className="input"
                placeholder={editingMethod?.method_name || 'Stripe'}
              />
              <label style={{ marginTop: '8px' }}>{t('paymentMethodNameEn')}</label>
              <input
                type="text"
                value={editForm.name_en || ''}
                onChange={(e) => setEditForm({ ...editForm, name_en: e.target.value })}
                className="input"
                placeholder="Stripe"
              />
              <label style={{ marginTop: '8px' }}>{t('paymentMethodNameDe')}</label>
              <input
                type="text"
                value={editForm.name_de || ''}
                onChange={(e) => setEditForm({ ...editForm, name_de: e.target.value })}
                className="input"
                placeholder="Stripe"
              />
            </div>

            <div className="form-group">
              <label>{t('paymentMethodDisplayMode')}</label>
              <div className="display-mode-options">
                <label>
                  <input
                    type="radio"
                    name="display_mode"
                    value="name"
                    checked={editForm.display_mode === 'name'}
                    onChange={(e) => setEditForm({ ...editForm, display_mode: e.target.value })}
                  />
                  <span>{t('paymentMethodDisplayName')}</span>
                </label>
                <label>
                  <input
                    type="radio"
                    name="display_mode"
                    value="image"
                    checked={editForm.display_mode === 'image'}
                    onChange={(e) => setEditForm({ ...editForm, display_mode: e.target.value })}
                  />
                  <span>{t('paymentMethodDisplayImage')}</span>
                </label>
                <label>
                  <input
                    type="radio"
                    name="display_mode"
                    value="name_and_image"
                    checked={editForm.display_mode === 'name_and_image'}
                    onChange={(e) => setEditForm({ ...editForm, display_mode: e.target.value })}
                  />
                  <span>{t('paymentMethodDisplayNameAndImage')}</span>
                </label>
              </div>
            </div>

            {(editForm.display_mode === 'image' || editForm.display_mode === 'name_and_image') && (
              <div className="form-group">
                <label>{t('paymentMethodImageUpload')}</label>
                <div className="payment-method-image-upload">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleImageUpload}
                    disabled={uploadingImage}
                    className="payment-method-file-input"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="btn btn-secondary btn-sm"
                  >
                    {uploadingImage ? t('uploading') : t('paymentMethodImageSelect')}
                  </button>
                  {editForm.image_url && (
                    <>
                      <button
                        type="button"
                        onClick={removeImage}
                        className="btn btn-secondary btn-sm payment-method-remove-image"
                      >
                        {t('paymentMethodImageRemove')}
                      </button>
                      <div className="payment-method-image-preview">
                        <img src={editForm.image_url} alt="" onError={(e) => { e.target.style.display = 'none'; }} />
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Konfigūracijos laukai */}
            {getMethodConfigFields(editingMethod.method_key).length > 0 && (
              <div className="config-section">
                <h3>{t('paymentMethodConfiguration')}</h3>
                {getMethodConfigFields(editingMethod.method_key).map((field) => {
                  const label = t(field.labelKey);
                  const placeholder = t('paymentMethodEnterPlaceholder').replace('{label}', label);
                  return (
                    <div key={field.key} className="form-group">
                      <label>{label}</label>
                      {field.type === 'textarea' ? (
                        <textarea
                          value={editForm.config_data[field.key] || ''}
                          onChange={(e) => handleConfigChange(field.key, e.target.value)}
                          className="input"
                          rows="4"
                          placeholder={placeholder}
                        />
                      ) : field.type === 'checkbox' ? (
                        <label>
                          <input
                            type="checkbox"
                            checked={editForm.config_data[field.key] || false}
                            onChange={(e) => handleConfigChange(field.key, e.target.checked)}
                          />
                          <span>{label}</span>
                        </label>
                      ) : (
                        <input
                          type={field.type}
                          value={editForm.config_data[field.key] || ''}
                          onChange={(e) => handleConfigChange(field.key, e.target.value)}
                          className="input"
                          placeholder={placeholder}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Stripe: papildomi būdai – Kortelė, Apple Pay, Google Pay (pavadinimai ir paveikslėliai) */}
            {editingMethod.method_key === 'stripe' && (
              <div className="config-section config-section-stripe-quick">
                <h3>{t('stripeQuickMethodsTitle')}</h3>
                <p className="form-hint">{t('stripeQuickMethodsHint')}</p>
                <input
                  ref={quickImageInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="payment-method-file-input"
                  onChange={(e) => {
                    if (quickImageTarget) {
                      const key = quickImageTarget === 'card' ? 'stripe_card_image_url' : quickImageTarget === 'apple_pay' ? 'stripe_apple_pay_image_url' : 'stripe_google_pay_image_url';
                      handleQuickImageUpload(e, key);
                    }
                  }}
                />
                {[
                  { id: 'card', titleKey: 'stripeQuickCardTitle', nameLt: 'stripe_card_name_lt', nameEn: 'stripe_card_name_en', nameDe: 'stripe_card_name_de', imageKey: 'stripe_card_image_url' },
                  { id: 'apple_pay', titleKey: 'stripeQuickApplePayTitle', nameLt: 'stripe_apple_pay_name_lt', nameEn: 'stripe_apple_pay_name_en', nameDe: 'stripe_apple_pay_name_de', imageKey: 'stripe_apple_pay_image_url' },
                  { id: 'google_pay', titleKey: 'stripeQuickGooglePayTitle', nameLt: 'stripe_google_pay_name_lt', nameEn: 'stripe_google_pay_name_en', nameDe: 'stripe_google_pay_name_de', imageKey: 'stripe_google_pay_image_url' },
                ].map((block) => (
                  <div key={block.id} className="stripe-quick-block">
                    <h4 className="stripe-quick-block-title">{t(block.titleKey)}</h4>
                    <div className="form-group">
                      <label>{t('paymentMethodNameLt')}</label>
                      <input
                        type="text"
                        value={editForm.config_data?.[block.nameLt] || ''}
                        onChange={(e) => handleConfigChange(block.nameLt, e.target.value)}
                        className="input"
                        placeholder={t(block.titleKey)}
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('paymentMethodNameEn')}</label>
                      <input
                        type="text"
                        value={editForm.config_data?.[block.nameEn] || ''}
                        onChange={(e) => handleConfigChange(block.nameEn, e.target.value)}
                        className="input"
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('paymentMethodNameDe')}</label>
                      <input
                        type="text"
                        value={editForm.config_data?.[block.nameDe] || ''}
                        onChange={(e) => handleConfigChange(block.nameDe, e.target.value)}
                        className="input"
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('paymentMethodImageUpload')}</label>
                      <div className="payment-method-image-upload">
                        <button
                          type="button"
                          onClick={() => { setQuickImageTarget(block.id); quickImageInputRef.current?.click(); }}
                          disabled={uploadingImage}
                          className="btn btn-secondary btn-sm"
                        >
                          {uploadingImage && quickImageTarget === block.id ? t('uploading') : t('paymentMethodImageSelect')}
                        </button>
                        {editForm.config_data?.[block.imageKey] && (
                          <>
                            <button
                              type="button"
                              onClick={() => handleConfigChange(block.imageKey, '')}
                              className="btn btn-secondary btn-sm payment-method-remove-image"
                            >
                              {t('paymentMethodImageRemove')}
                            </button>
                            <div className="payment-method-image-preview">
                              <img src={editForm.config_data[block.imageKey]} alt="" onError={(e) => { e.target.style.display = 'none'; }} />
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Banko pavedimas: banko sąskaitos (dinaminis sąrašas) */}
            {editingMethod.method_key === 'bank_transfer' && (
              <div className="config-section config-section-bank-transfer">
                <h3>{t('paymentMethodConfiguration')}</h3>
                <p className="form-hint bank-transfer-hint">{t('paymentConfigBankAccountsHint')}</p>
                <div className="bank-accounts-list">
                  {bankAccounts.map((row, index) => (
                    <div key={index} className="bank-account-row">
                      <div className="bank-account-row-fields">
                        <div className="form-group">
                          <label>{t('paymentConfigIban')}</label>
                          <input
                            type="text"
                            value={row.iban || ''}
                            onChange={(e) => updateBankAccount(index, 'iban', e.target.value)}
                            className="input"
                            placeholder="LT..."
                          />
                        </div>
                        <div className="form-group">
                          <label>{t('paymentConfigBankName')}</label>
                          <input
                            type="text"
                            value={row.bank_name || ''}
                            onChange={(e) => updateBankAccount(index, 'bank_name', e.target.value)}
                            className="input"
                            placeholder={t('paymentConfigBankNamePlaceholder')}
                          />
                        </div>
                        <div className="form-group">
                          <label>{t('paymentConfigCompanyName')}</label>
                          <input
                            type="text"
                            value={row.company_name || ''}
                            onChange={(e) => updateBankAccount(index, 'company_name', e.target.value)}
                            className="input"
                            placeholder={t('paymentConfigCompanyNamePlaceholder')}
                          />
                        </div>
                      </div>
                      <div className="bank-account-row-actions">
                        <button
                          type="button"
                          onClick={() => removeBankAccount(index)}
                          className="btn btn-secondary btn-sm"
                          disabled={bankAccounts.length <= 1}
                          title={t('paymentConfigRemoveAccount')}
                        >
                          {t('paymentConfigRemoveAccount')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <button type="button" onClick={addBankAccount} className="btn btn-secondary btn-add-account">
                  {t('paymentConfigAddBankAccount')}
                </button>
                <div className="form-group" style={{ marginTop: '1.5rem' }}>
                  <label>{t('paymentConfigReferencePrefix')}</label>
                  <input
                    type="text"
                    value={editForm.config_data?.reference_prefix || ''}
                    onChange={(e) => handleConfigChange('reference_prefix', e.target.value)}
                    className="input"
                    placeholder={t('paymentConfigReferencePrefixPlaceholder')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('paymentConfigInstructionsLt')}</label>
                  <textarea
                    value={editForm.config_data?.instructions_lt || ''}
                    onChange={(e) => handleConfigChange('instructions_lt', e.target.value)}
                    className="input"
                    rows={3}
                    placeholder={t('bankTransferDefaultInstructionsLt')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('paymentConfigInstructionsEn')}</label>
                  <textarea
                    value={editForm.config_data?.instructions_en || ''}
                    onChange={(e) => handleConfigChange('instructions_en', e.target.value)}
                    className="input"
                    rows={3}
                    placeholder={t('bankTransferDefaultInstructionsEn')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('paymentConfigInstructionsDe')}</label>
                  <textarea
                    value={editForm.config_data?.instructions_de || ''}
                    onChange={(e) => handleConfigChange('instructions_de', e.target.value)}
                    className="input"
                    rows={3}
                    placeholder={t('bankTransferDefaultInstructionsDe')}
                  />
                </div>
              </div>
            )}

            <div className="modal-actions">
              <button
                type="button"
                onClick={() => setEditingMethod(null)}
                className="btn btn-secondary"
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={updateMutation.isLoading}
                className="btn btn-primary"
              >
                {updateMutation.isLoading ? t('saving') : t('save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPaymentMethods;
